import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import adminService from '@/services/adminService.ts';
import type { AdminUserSummary, ResetSalespersonStatsResponse } from '@/types/admin.ts';

interface ResetSalespersonStatsModalProps {
  target: AdminUserSummary | null;
  onClose: () => void;
  onDone: (result: ResetSalespersonStatsResponse) => void | Promise<void>;
}

const DEFAULT_BASE_SCORE = 7.0;
const DEFAULT_SESSION_COUNT = 3;

export default function ResetSalespersonStatsModal({
  target,
  onClose,
  onDone,
}: ResetSalespersonStatsModalProps) {
  const { t } = useTranslation();
  const [baseScore, setBaseScore] = useState(DEFAULT_BASE_SCORE);
  const [sessionCount, setSessionCount] = useState(DEFAULT_SESSION_COUNT);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOpen = target !== null;

  useEffect(() => {
    if (target) {
      setBaseScore(DEFAULT_BASE_SCORE);
      setSessionCount(DEFAULT_SESSION_COUNT);
      setError(null);
      setSubmitting(false);
    }
  }, [target]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target) return;
    setError(null);

    if (!Number.isFinite(baseScore) || baseScore < 1.0 || baseScore > 10.0) {
      setError(t('superuser.admin.users.resetModal.invalidScore', {
        defaultValue: 'Puntuación base debe estar entre 1.0 y 10.0.',
      }));
      return;
    }
    if (!Number.isInteger(sessionCount) || sessionCount < 1 || sessionCount > 10) {
      setError(t('superuser.admin.users.resetModal.invalidCount', {
        defaultValue: 'Número de sesiones debe estar entre 1 y 10.',
      }));
      return;
    }

    setSubmitting(true);
    try {
      const result = await adminService.resetSalespersonStats(target.id, {
        baseScore,
        sessionCount,
      });
      await onDone(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('superuser.admin.users.resetModal.title', { defaultValue: 'Reset stats de usuario' })}
          </DialogTitle>
          <DialogDescription>
            {target && t('superuser.admin.users.resetModal.description', {
              name: `${target.firstName} ${target.lastName}`,
              email: target.email,
              defaultValue: 'Borrará todas las sesiones de {{name}} ({{email}}) y generará nuevas sesiones de baseline.',
            })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {target && t('superuser.admin.users.resetModal.warning', {
                count: target.sessionCount ?? 0,
                defaultValue: 'Esto eliminará {{count}} sesión(es) existente(s) y sus reportes asociados. Acción irreversible.',
              })}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t('superuser.admin.users.resetModal.baseScoreLabel', { defaultValue: 'Puntuación base' })}
              </label>
              <input
                type="number"
                min={1}
                max={10}
                step={0.1}
                value={baseScore}
                onChange={(e) => setBaseScore(Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('superuser.admin.users.resetModal.baseScoreHint', {
                  defaultValue: 'Entre 1.0 y 10.0. Por defecto 7.0 = «bueno».',
                })}
              </p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t('superuser.admin.users.resetModal.sessionCountLabel', { defaultValue: 'Nº sesiones a generar' })}
              </label>
              <input
                type="number"
                min={1}
                max={10}
                step={1}
                value={sessionCount}
                onChange={(e) => setSessionCount(Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('superuser.admin.users.resetModal.sessionCountHint', {
                  defaultValue: 'Entre 1 y 10. Por defecto 3.',
                })}
              </p>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="destructive" disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {t('superuser.admin.users.resetModal.confirm', { defaultValue: 'Borrar y regenerar' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
